import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';

import { Deferred } from '../../classes/deferred';
import { environment } from '../../environment';
import { ESRI } from '../../esri-loader-util';
import { BooleanConverter, InputConverter, NumberConverter } from '../../inputConverter';
import { GisToolsService } from '../../services/gis-tools.service';
import { BaseComponent } from '../base.component';

let Graphic: NonNullable<__esri.GraphicConstructor>;
let Point: NonNullable<__esri.PointConstructor>;
let WebMercatorUtils: NonNullable<__esri.webMercatorUtils>;

const googleMapsApiLoadCheckInterval = 25;
const googleMapsApiLoadCheckMax = 15000;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-street-view',
    templateUrl: './street-view.component.html',
    styleUrls: ['../sharedStyles/base.scss', './street-view.component.scss']
})
export class StreetViewComponent extends BaseComponent implements OnInit {
    @ViewChild('streetView') private readonly streetView: ElementRef<HTMLDivElement>;

    @Input() mapViewRef: NonNullable<__esri.MapView>;

    @Input() @InputConverter(BooleanConverter)
    get active() { return this._active; }
    set active(value: boolean) {
        value ? this.activate() : this.deactivate();
    }
    private _active;

    @Input() public setRms = (rms) => {
        this.onRmsChange(rms);
    }

    @Input() @InputConverter(NumberConverter)
    get lat(): number { return this._lat; }
    set lat(value) {
        if (this.active && value && value !== this._lat) {
            this.setStreetView(value, this.long, this.heading, this.pitch);
        }
        this._lat = value;
    }
    private _lat: number;

    @Input() @InputConverter(NumberConverter)
    get long(): number { return this._long; }
    set long(value) {
        if (this.active && value && value !== this._long) {
            this.setStreetView(this.lat, value, this.heading, this.pitch);
        }
        this._long = value;
    }
    private _long: number;

    @Input() @InputConverter(NumberConverter)
    get heading(): number { return this._heading; }
    set heading(value) {
        if (this.active && value && value !== this._heading) {
            this.setStreetView(this.lat, value, this.heading, this.pitch);
        }
        this._heading = value;
    }
    private _heading: number;

    @Input() @InputConverter(NumberConverter) pitch: number;
    @Input() @InputConverter(NumberConverter) debounceTime: number = 500;
    @Input() @InputConverter(NumberConverter) precision: number = 6;

    @Input() @InputConverter(BooleanConverter) autoPanMap = false;
    @Input() @InputConverter(BooleanConverter) openToDefaultLocation = true;

    @Output() readonly positionChange: Subject<any> = new Subject<any>();

    arrowColorR: number = 40;
    arrowColorG: number = 96;
    arrowColorB: number = 144;
    arrowGraphic: __esri.Graphic;
    arrowGraphicsLayer: __esri.GraphicsLayer;
    arrowPath: string = 'M192 320L0 576h128v128h128V576h128L192 320z';
    arrowSize: string = '16px';
    arrowSymbol: any;
    found: boolean;
    location = { latitude: 0, longitude: 0, heading: 0 };
    markerGeometry;
    panorama;
    resizeObserver: ResizeObserver;
    rmsChangeFlag: boolean = true;
    rmsDefaultOffset: string = '40'; // Try to find a SV image on the segment requested
    streetViewHandler;
    streetViewService: google.maps.StreetViewService;

    constructor(
        _vcf: ViewContainerRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly gisToolService: GisToolsService) {
        super(_vcf, true);
    }

    async ngOnInit() {
        await Promise.all([this.loadGoogleMapScript(), this.loadESRIClasses()]);
    }

    async activate() {
        if (!this.mapViewRef) {
            this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        }

        if (this.lat && this.long && this.openToDefaultLocation) {
            this.setStreetView(this.lat, this.long, this.heading, this.pitch);
        }

        this.streetViewHandler = this.mapViewRef.on('click', this.onMapClick.bind(this));

        this.resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target === this.streetView.nativeElement && this.panorama) {
                    google.maps.event.trigger(this.panorama, 'resize');
                }
            }
        });
        this.resizeObserver.observe(this.streetView.nativeElement);

        this._active = true;
    }

    deactivate() {
        if (this.streetViewHandler) {
            this.streetViewHandler.remove();
        }

        if (this.arrowGraphic) {
            this.mapViewRef.graphics.remove(this.arrowGraphic);
        }

        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.streetView.nativeElement);
        }

        this._active = false;
    }

    private onMapClick(event) {
        const geographicPoint = <__esri.Point>WebMercatorUtils.webMercatorToGeographic(event.mapPoint);
        this.setStreetView(geographicPoint.y, geographicPoint.x, this.heading, this.pitch);
    }

    async loadGoogleMapScript() {
        if (!document.getElementById('MC-GoogleMapsApi') && !(window.google && window.google.maps)) {
            if (!window.googleMapsApiCallback) {
                window.googleMapsApiCallback = function() {
                    //console.log('Google Maps API loaded.')
                };
            }
            const script = document.createElement('script');
            script.setAttribute('src', `${environment.googleMapApiUrl}&${environment.googleMapApiKey}&callback=googleMapsApiCallback`);
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('id', 'MC-GoogleMapsApi');
            document.head.appendChild(script);
            // Wait for the Google Maps api to load
            const deferred = new Deferred<void>();
            let apiChecks = 0;
            const apiCheckHandler = setInterval(() => {
                apiChecks++;
                if (apiChecks > (googleMapsApiLoadCheckMax / googleMapsApiLoadCheckInterval)) {
                    clearInterval(apiCheckHandler);
                    deferred.reject();
                } else if (window.google && window.google.maps) {
                    clearInterval(apiCheckHandler);
                    deferred.resolve();
                }
            }, googleMapsApiLoadCheckInterval);
            return deferred.promise;
        }
    }

    async loadESRIClasses() {
        if (!Graphic || !Point || !WebMercatorUtils) {
            [Graphic, Point, WebMercatorUtils] = await Promise.all([ESRI.Graphic, ESRI.Point, ESRI.WebMercatorUtils]);
        }
        this.initializeStreetViewMarker();
    }

    private initializeStreetViewMarker() {
        if (!this.arrowSymbol) {
            const streetViewArrowColor: number[] = [this.arrowColorR, this.arrowColorG, this.arrowColorB];
            this.arrowSymbol = {
                type: 'simple-marker',
                color: streetViewArrowColor,
                size: this.arrowSize,
                path: this.arrowPath,
                angle: 0,
                outline: {
                    color: streetViewArrowColor,
                    width: 1
                }
            };
        }
    }

    private initializeGooglePanorama(position, pov) {
        this.panorama = new google.maps.StreetViewPanorama(this.streetView.nativeElement, {
            imageDateControl: true,
            position,
            pov
        });
        this.panorama.addListener('position_changed', this.onStreetViewChange.bind(this));
        this.panorama.addListener('pov_changed', this.onStreetViewChange.bind(this));
        this.panorama.addListener('status_changed', this.onStreetViewStatusChange.bind(this));
    }

    async onRmsChange(rms) {
        if (!rms.offset || rms.offset === 0) {
            rms.offset = this.rmsDefaultOffset;
        }
        const offset = (rms.offset && rms.offset !== '0') ? rms.offset : this.rmsDefaultOffset;
        const latLong = await this.gisToolService.getLatLongForOffset(rms.countyCode, rms.stateRouteNumber, rms.segmentNumber, offset);
        if (latLong) {
            this.rmsChangeFlag = false;  // If RMS component sent the update, done have it refresh itself after navigation is done.
            this.setStreetView(latLong.latitude, latLong.longitude, this.heading, this.pitch);
        }
    }

    private setStreetView(latitude: number, longitude: number, heading: number = 0, pitch: number = 0) {
        if (latitude && longitude) {
            const position = new google.maps.LatLng(latitude, longitude);
            const pov: google.maps.StreetViewPov = { heading, pitch };
            this.location = { latitude, longitude, heading };
            if (this.panorama) {
                const curPos = this.panorama.getPosition();
                const curPov = this.panorama.getPov();
                if (curPos.lat() !== latitude || curPos.lng() !== longitude) {
                    this.panorama.setPosition(position);
                }
                if (curPov.heading !== heading || curPov.pitch !== pitch) {
                    this.panorama.setPov(pov);
                }
            } else {
                this.initializeGooglePanorama(position, pov);
            }

            if (this.autoPanMap) {
                this.mapViewRef.goTo({
                    target: new Point(this.location),
                    animate: true
                });
            }
        }
    }

    private onStreetViewChange() {
        const position = this.panorama.getPosition();
        const pov = this.panorama.getPov();
        const heading = pov.heading;
        const latitude = position.lat();
        const longitude = position.lng();

        // This event fires multiple times per single update, wait until it actually changes to a new lat/long/heading before handling it
        if (!this.location || latitude !== this.location.latitude || longitude !== this.location.longitude || heading !== this.location.heading) {
            this.setStreetViewMarker(latitude, longitude, heading);
            this.positionChange.next({ latitude, longitude, heading, rmsChangeFlag: this.rmsChangeFlag });
            this.location = { latitude, longitude, heading };
            this.rmsChangeFlag = true;
        }
    }

    private onStreetViewStatusChange() {
        this.found = this.panorama.getStatus() === 'OK';
        this.panorama.setVisible(this.found);
        this.cdr.detectChanges();
    }

    private setStreetViewMarker(latitude: number, longitude: number, heading: number = 0) {
        this.markerGeometry = { type: 'point', latitude, longitude };
        this.arrowSymbol.angle = heading;

        if (this.arrowGraphic) {
            this.mapViewRef.graphics.remove(this.arrowGraphic);
        }

        this.arrowGraphic = new Graphic({
            geometry: this.markerGeometry,
            symbol: this.arrowSymbol
        });

        this.mapViewRef.graphics.add(this.arrowGraphic);
    }
}
